/**
 * Documents tool — MCP read surface for verified brief snapshots.
 *
 * WP-371 S4 (Seam Slice — agent surface side). Compound tool with action-enum
 * dispatch (BR-72 / STD-86). Currently exposes a single read action;
 * additional read actions (e.g. list-recent-verified, get-by-runId) can join
 * the same `documents` surface as the kernel grows.
 *
 * Read-only. v1.1 explicitly excludes any mutation from MCP — agents observe
 * the verification audit trail; they do not write to it.
 *
 * Workspace context is enforced by the HTTP gateway from the API key (BR-18).
 * The MCP tool never accepts or forwards a workspaceId — `kernelQuery`
 * injects it server-side.
 *
 * Chain: WP-371, BR-72 (compound action enum), STD-86 (compound tool standard),
 * BR-18 (gateway enforces workspace from credential), BR-150 (no slug branching).
 */

import type { GatewayRouteReturnByName } from "@productbrain/kernel-client";
import { z } from "zod/v3";
import { kernelQuery } from "../client.js";
import { success } from "../envelope.js";

// ─── Schema ──────────────────────────────────────────────────────────────────

const DOCUMENTS_ACTIONS = ["get-last-verified-brief"] as const;

export const documentsSchema = z.object({
  action: z.enum(DOCUMENTS_ACTIONS).describe(
    "'get-last-verified-brief': fetch the most recent verified brief snapshot for a (templateId, scopeKey) pair. Returns the verified summary so agents can build delta narratives ('since you last verified, X workstreams advanced').",
  ),
  templateId: z.string().max(200).describe(
    "Brief template identifier — currently 'steering-brief' is the only registered template.",
  ),
  scopeKey: z.string().max(200).describe(
    "Canonical scope key. Use 'workspace:<workspaceId>' for the full workspace brief, or 'initiative:<INI-ID>' for an initiative-scoped brief. The same formula is applied at write time (chainwork/docKernel/scopeKey.ts), so passing the wrong shape returns exists:false.",
  ),
});

// ─── Output types (mirrors getLastVerifiedBrief return shape) ───────────────

interface VerifiedWorkstreamSnapshot {
  entryId: string;
  name: string;
  status: string;
  whatChanged: string;
  updatedAtAtVerify: number;
}

interface VerifiedSummary {
  verifiedAtGlance: string;
  verifiedWorkstreams: VerifiedWorkstreamSnapshot[];
  citedEntryIds: string[];
  chainSyncedAt: number;
  verifiedAt: number;
  verifiedBy: string;
}

/**
 * Discriminated read result. Matches the chainwork query return shape exactly.
 * Agents key on `exists` first, then on `summary` presence to decide whether
 * a delta narrative is possible.
 */
type LastVerifiedBriefResult =
  | { exists: false }
  | {
      exists: true;
      summary: VerifiedSummary | undefined;
      verifiedAt: number;
    };

// WP-484 S3 (clean break, TEN-2157): the standalone `documents` tool retired
// here. handleGetLastVerifiedBrief stays live — context.ts (the `context`
// compound, action=last-verified-brief) is its only remaining caller.

// ─── Handler ────────────────────────────────────────────────────────────────

export async function handleGetLastVerifiedBrief(
  templateId: string,
  scopeKey: string,
) {
  // kernelQuery injects workspaceId from the API-key context (BR-18).
  const result = await kernelQuery<GatewayRouteReturnByName<"chainwork.getLastVerifiedBrief">>(
    "chainwork.getLastVerifiedBrief",
    { templateId, scopeKey },
  );

  if (!result.exists) {
    const text =
      `No verified brief found for template '${templateId}', scope '${scopeKey}'.\n\n` +
      `This is normal for a never-verified initiative or workspace. ` +
      `If you expected a row here, check that the scope key matches the format ` +
      `produced at write time: 'workspace:<workspaceId>' or 'initiative:<INI-ID>'.`;
    return {
      content: [{ type: "text" as const, text }],
      structuredContent: success(
        `No verified brief found for ${templateId} / ${scopeKey}.`,
        { exists: false },
      ),
    };
  }

  const verifiedAtIso = new Date(result.verifiedAt).toISOString();

  if (result.summary === undefined) {
    // Legacy backfilled row — no snapshot data was captured at write time.
    const text =
      `Verified brief found for template '${templateId}', scope '${scopeKey}', ` +
      `verified at ${verifiedAtIso}.\n\n` +
      `This row pre-dates snapshot capture (legacy backfill), so no summary is ` +
      `available. The verification timestamp itself is still authoritative — ` +
      `you can reason about staleness, just not delta narratives.`;
    return {
      content: [{ type: "text" as const, text }],
      structuredContent: success(
        `Legacy verified brief at ${verifiedAtIso} (no summary).`,
        { exists: true, summary: undefined, verifiedAt: result.verifiedAt },
      ),
    };
  }

  // Full S4 row with snapshot.
  const summary = result.summary;
  const lines: string[] = [
    `## Last verified brief — ${templateId} / ${scopeKey}`,
    `Verified at: ${verifiedAtIso} by ${summary.verifiedBy}`,
    `Chain synced at: ${new Date(summary.chainSyncedAt).toISOString()}`,
    "",
    `### At a glance (verbatim)`,
    summary.verifiedAtGlance || "(empty)",
    "",
    `### Workstreams at verification (${summary.verifiedWorkstreams.length})`,
  ];
  for (const ws of summary.verifiedWorkstreams) {
    lines.push(
      `- **${ws.entryId}** ${ws.name} — status: ${ws.status}` +
        (ws.whatChanged ? ` — ${ws.whatChanged}` : ""),
    );
  }
  if (summary.citedEntryIds.length > 0) {
    lines.push("");
    lines.push(`### Cited entries (${summary.citedEntryIds.length})`);
    lines.push(summary.citedEntryIds.join(", "));
  }

  return {
    content: [{ type: "text" as const, text: lines.join("\n") }],
    structuredContent: success(
      `Verified brief found at ${verifiedAtIso} with ${summary.verifiedWorkstreams.length} workstream snapshot(s).`,
      {
        exists: true,
        summary,
        verifiedAt: result.verifiedAt,
      },
    ),
  };
}
